import type { CollectionSummaryJSONObject, FormSummaryJSONObject } from '@ikegps/core-api';
import { isPLSImportType, plaTypeFromMetadata } from '@ikegps/core-shared';
import { observable, makeObservable } from 'mobx';
import { InputState } from '../../components/input/input.state';
import { ModalFormComponent, type ModalFormComponentCtx } from '../../components/modal.form/modal.form.component';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { AppStore } from '../../store';
import { CollectionFormMigrateModalCtrl, type FormMigrateFailure } from './collection.form.migrate.modal.ctrl';
import { CSS, Style } from './collection.form.migrate.modal.style';

addStyles(Style);

export class CollectionFormMigrateModal extends ModalFormComponent {
	private ctrl: CollectionFormMigrateModalCtrl;
	private departmentForms: FormSummaryJSONObject[] = [];
	private errorState: InputState;
	private loadingState: InputState;
	@observable errors: FormMigrateFailure[] = [];
	@observable private selectedFormRevision: string | null = null;

	constructor(ctx: ModalFormComponentCtx, collections: CollectionSummaryJSONObject[]) {
		super(ctx);
		makeObservable(this);

		this.ctrl = new CollectionFormMigrateModalCtrl(collections);

		this.errorState = new InputState(T.ERROR);
		this.loadingState = new InputState(T.LOADING);
		this.addState(this.errorState);
		this.addState(this.loadingState);

		AppStore.FormSummary.refresh();
	}

	get nodes(): MithrilVNode {
		this.departmentForms = AppStore.FormSummary.all;

		if (this.departmentForms.length === 0) {
			this.loadingState.setError(T.LOADING);
			return Spinner.view();
		}

		const collectionFormRevision = this.ctrl.collections[0]?.formRevision;
		const sourceForm = collectionFormRevision ? AppStore.Form.get(collectionFormRevision) : null;
		const sourceIsPLS = Boolean(sourceForm?.metadata.isPLSCADDForm());

		// PLS sources may only migrate to other PLS forms; everything else lists PLA forms.
		const filteredForms = this.departmentForms.filter((targetForm) => {
			if (!targetForm.metadata || targetForm.revision === collectionFormRevision) {
				return false;
			}

			if (sourceIsPLS) {
				// PLS has known broken forms which we do not want to include as migration target options
				if (isPLSImportType(targetForm.metadata)) {
					return targetForm.revision === targetForm.version;
				}

				return false;
			}

			return plaTypeFromMetadata(targetForm.metadata) != null;
		});

		if (filteredForms.length === 0) {
			return m('div', Translate(T.COLLECTION_MIGRATE_FORM_BODY_NO_FORMS));
		}

		this.loadingState.reset();

		if (!this.selectedFormRevision) {
			this.selectedFormRevision = filteredForms[0]!.revision;
		}

		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.content }, [
				m('div', { className: CSS.warningTitle }, Translate(T.COLLECTION_MIGRATE_FORM_BODY_WARNING_TITLE)),
				m('div', { className: CSS.warningBody }, Translate(T.COLLECTION_MIGRATE_FORM_BODY_WARNING_DESCRIPTION_PT1)),
				m('div', { className: CSS.body }, [
					m('div', Translate(T.COLLECTION_MIGRATE_FORM_BODY_WARNING_DESCRIPTION_PT2)),
					m('ul', [
						m('li', Translate(T.COLLECTION_MIGRATE_FORM_BODY_WARNING_DESCRIPTION_PT3)),
						m('li', Translate(T.COLLECTION_MIGRATE_FORM_BODY_WARNING_DESCRIPTION_PT4)),
					]),
					m('div', Translate(T.COLLECTION_MIGRATE_FORM_BODY)),
				]),
			]),
			m(
				'select',
				{
					onchange: (e) => {
						if ('target' in e && e.target && 'value' in e.target && typeof e.target.value === 'string') {
							this.selectedFormRevision = e.target.value;
						}
					},
				},
				filteredForms.map((form) =>
					m('option', { value: form.revision }, `${form.displayname} (V${form.displayversion})`)
				)
			),
		]);
	}

	override failToChange() {
		this.errors = this.ctrl.failures;
		this.errorState.setError(T.ERROR);
		NotificationService.error({ key: T.COLLECTION_MIGRATE_FORM_FAIL });
	}

	override async submit() {
		if (this.selectedFormRevision) {
			await this.ctrl.updateFormCollections(this.selectedFormRevision);
		}
	}
}
